import { trigger, state, style, transition, animate } from '@angular/animations';
import { Component, computed, EventEmitter, Input, Output, signal, WritableSignal, SimpleChanges, inject } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Observable, of, combineLatest } from 'rxjs';
import { map, startWith, tap } from 'rxjs/operators';


import { PermissionBaseComponent } from '../../../../../core/abstracts/permission-base.component';

import { ItemMenu, SubMenu } from "../../models/internal/item-menu-model";

import { PermissionService } from '../../../../services/common/permission.service';
import { NotificationService } from '../../../../services/common/notification.service';
import { AitrusSsoService } from '../../../../services/common/aitrus-sso.service';
import { UserService }
from '../../../../services/common/user.service';

import { TooltipDirective } from '../../../../../modules/quote-v2/components/tooltip/tooltip.directive';
import { PharmasyncService } from '../../../pharma-sync/service/pharmasync.service';
import { HeaderService } from '../../services/header.service';
import { MenuFavoritesService } from '../../services/menu-favorites.service';

enum DisplayState {
  Enabled,
  Submenu,
  DisabledByLicense,
  DisabledByPharmaSync,
  DisabledByCertificate,
  Hidden
}

interface MenuItemViewModel {
  original: ItemMenu | SubMenu;
  title: string;
  link?: string;
  externalLink?: string;
  aitrusRedirect?: string;
  icon?: SafeHtml;
  state$: Observable<DisplayState>;
  tooltip$: Observable<string>;
  submenu?: MenuItemViewModel[];
  blockedReason$?: Observable<string | null>;
}

@Component({
    selector: 'app-item-menu',
    imports: [
        CommonModule,
        RouterLink,
        RouterLinkActive,
        TooltipDirective,
        MatListModule,
        MatIconModule,
        MatMenuModule
    ],
    animations: [
        trigger('rotateArrow', [
            state('collapsed', style({ transform: 'rotate(0deg)' })),
            state('expanded', style({ transform: 'rotate(90deg)' })),
            transition('collapsed <=> expanded', animate('300ms ease-out'))
        ]),
        // O translateX(24px) que havia aqui compensava a classe `translate-x-6` do
        // markup antigo. Sem ela, ele deslocava o submenu inteiro durante os 300ms e
        // deixava o alinhamento saltar ao terminar. O recuo agora é só do CSS.
        trigger('fadeSlideInOut', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(-10px)' }),
                animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0px)' }))
            ]),
            transition(':leave', [
                animate('300ms ease-out', style({ opacity: 0, transform: 'translateY(-10px)' }))
            ])
        ])
    ],
    templateUrl: './item-menu.component.html',
    styleUrls: ['./item-menu.component.scss']
})
export class ItemMenuComponent extends PermissionBaseComponent {
  @Input() itemMenu: ItemMenu = {} as ItemMenu;
  @Output() close = new EventEmitter<void>();
  @Output() itemClick = new EventEmitter<{ title: string; link?: string; externalLink?: string }>();

  public DisplayState = DisplayState;

  public viewModel: MenuItemViewModel | null = null;
  public expanded: WritableSignal<boolean> = signal(false);

  private readonly sanitizer = inject(DomSanitizer);
  private readonly router = inject(Router);
  private readonly breakpointObs = inject(BreakpointObserver);
  private readonly headerService = inject(HeaderService);
  private readonly userService = inject(UserService);
  private readonly aitrusSso = inject(AitrusSsoService);
  private readonly notificationService = inject(NotificationService);
  public readonly favoritos = inject(MenuFavoritesService);
  public readonly pharmaSyncService = inject(PharmasyncService);


  constructor(permissionService: PermissionService) {
    super(permissionService);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['itemMenu'] && changes['itemMenu'].currentValue) { 
      this.viewModel = this._processMenuItem(changes['itemMenu'].currentValue);
    }
  }

  private toBooleanObservable(source: Observable<any> | boolean | null | undefined): Observable<boolean> {
    if (source instanceof Observable) {
      return source.pipe(map(val => !!val), startWith(false));
    } else {
      return of(!!source);
    }
  }

  private _processMenuItem(item: ItemMenu | SubMenu): MenuItemViewModel {
    const state$ = this._determineState(item);
    const isSubMenu = 'submenu' in item && !!item.submenu;

    return {
      original: item,
      title: item.title,
      link: item.link,
      externalLink: 'externalLink' in item ? item.externalLink : undefined,
      aitrusRedirect: item.aitrusRedirect,
      icon: 'icon' in item ? this.sanitizer.bypassSecurityTrustHtml(item.icon) : undefined,
      // --- MODIFIED LOGIC HERE ---
      state$: state$.pipe(
        map(s => {
          if (s === DisplayState.Hidden) {
            return DisplayState.Hidden; // If _determineState says it's hidden, respect that
          }
          return isSubMenu ? DisplayState.Submenu : s; // Otherwise, apply submenu logic
        })
      ),
      // --- END MODIFIED LOGIC ---
      tooltip$: state$.pipe(map(state => this._getTooltipForState(state))),
      submenu: 'submenu' in item && item.submenu ? item.submenu.map(sub => this._processMenuItem(sub)) : undefined,
      blockedReason$: item.blockedReason$
    };
  }

  private _determineState(item: ItemMenu | SubMenu): Observable<DisplayState> {
    const permissionObs = this.toBooleanObservable(item.permission$);

    // Casado pela rota, não pelo título: o título é rótulo e muda com a redação do menu,
    // e o caso especial some em silêncio junto com ele. Instalar o PharmaSync não
    // destrava o extrato — só ser licenciado — então este item não cai no ramo genérico,
    // que ofereceria o PharmaSync ao freemium.
    if (item.link === '/extrato-financeiro') {
      return combineLatest([
        this.toBooleanObservable(this.permissionService.isUserLicenciado()),
        this.toBooleanObservable(this.permissionService.isUserInterno())
      ]).pipe(
        map(([lic, intern]: [boolean, boolean]) => (lic || intern) ? DisplayState.Enabled : DisplayState.DisabledByLicense)
      );
    }

    return combineLatest([
      permissionObs,
      this.toBooleanObservable(this.pharmaSyncService.pharmasyncStatus$),
      this.toBooleanObservable(this.permissionService.isUserFreemiumOrOtimiza()),
      this.toBooleanObservable(this.permissionService.isUserLicenciado()),
      this.toBooleanObservable(this.permissionService.isUserInterno())
    ]).pipe(
      map(([isPermitted, hasPharmaSync, isFreemiumUser, isLicensedUser, isInternalUser]: [boolean, boolean, boolean, boolean, boolean]) => {
        if (isPermitted) {
          return DisplayState.Enabled;
        }
        if (item.hideWhenNoAccess) {
          return DisplayState.Hidden;
        }
        if (isFreemiumUser && !hasPharmaSync) {
          return DisplayState.DisabledByPharmaSync;
        }
        return DisplayState.DisabledByLicense;
      })
    );
  }

  private _getTooltipForState(state: DisplayState): string {
    switch (state) {
      case DisplayState.DisabledByLicense:
        return 'Bloqueado - É necessário ser licenciado do Grupo Hiper Saúde para ter acesso';
      case DisplayState.DisabledByPharmaSync:
        return 'Bloqueado - É necessário instalar o PharmaSync para ter acesso';
      case DisplayState.DisabledByCertificate:
        return 'Bloqueado - É necessário carregar o certificado A1 para ter acesso';
      default:
        return '';
    }
  }

  /** Menu recolhido na trilha de ícones: o mesmo sinal que o header usa para a largura. */
  public collapsed = computed(() => !this.headerService.isMenuVisible());

  /**
   * Um item com submenu não tem rota própria, então o routerLinkActive dele nunca
   * acende. Ele fica selecionado quando a rota atual é a de algum subitem — assim o
   * ícone marca a seção mesmo recolhido, onde o subitem sequer aparece.
   */
  public isSubmenuActive(vm: MenuItemViewModel): boolean {
    return !!vm.submenu?.some(sub => !!sub.link && this.router.isActive(sub.link, {
      paths: 'exact',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored'
    }));
  }

  /** Com o menu recolhido não há onde abrir o submenu embutido; vai para o flyout. */
  public hasFlyout(state: DisplayState | null): boolean {
    return state === DisplayState.Submenu && this.collapsed();
  }

  /** Estados em que o item aparece esmaecido e com cadeado, mas ainda clicável. */
  public isLocked(state: DisplayState | null): boolean {
    return state === DisplayState.DisabledByLicense
      || state === DisplayState.DisabledByPharmaSync
      || state === DisplayState.DisabledByCertificate;
  }

  public handleMainClick(vm: MenuItemViewModel, state: DisplayState, blockedReason?: string | null): void {
    // Emitir evento de click com informações do item
    this.itemClick.emit({
      title: vm.title,
      link: vm.link,
      externalLink: vm.externalLink
    });

    switch (state) {
      case DisplayState.Enabled:
        if (blockedReason) {
          this._notifyBlocked(vm, blockedReason);
          break;
        }

        // Links internos navegam pelo routerLink do próprio <a>; aqui só resta
        // fechar o menu no mobile e tratar os destinos externos.
        this._openExternalDestination(vm);
        this._closeOnMobile();
        break;
      case DisplayState.Submenu:
        this.expanded.update(current => !current);
        break;
      case DisplayState.DisabledByPharmaSync:
        this.pharmaSyncService.openModal();
        break;
      case DisplayState.DisabledByCertificate:
        this.router.navigate(['/certificado']).then(() => this._closeOnMobile());
        break;
    }
  }

  public handleSubmenuClick(vm: MenuItemViewModel, state: DisplayState, blockedReason?: string | null): void {
     // Emitir evento de click com informações do subitem
     this.itemClick.emit({
       title: vm.title,
       link: vm.link,
       externalLink: vm.externalLink
     });

     if (state === DisplayState.Enabled && blockedReason) {
        this._notifyBlocked(vm, blockedReason);
     } else if (state === DisplayState.Enabled) {
        this._openExternalDestination(vm);
        this._closeOnMobile();
     } else if (state === DisplayState.DisabledByCertificate) {
        this.router.navigate(['/certificado']).then(() => this._closeOnMobile());
     } else if (state === DisplayState.DisabledByPharmaSync) {
        this.pharmaSyncService.openModal();
     }
  }

  /**
   * A rota do routerLink, ou null quando o item não navega: item Aitrus e link externo
   * abrem aba em vez de navegar, e um item sem `link` ainda não tem destino — está no
   * menu só para ser visto. Sem o null, os três navegariam para string vazia. Item
   * bloqueado (`blockedReason$`) também não navega.
   */
  public rotaDe(vm: MenuItemViewModel, state: DisplayState | null, blockedReason?: string | null): string | null {
    const navega = state === DisplayState.Enabled && !blockedReason && !this.hasExternalDestination(vm);

    return navega && vm.link ? vm.link : null;
  }

  private hasExternalDestination(vm: MenuItemViewModel): boolean {
    return !!vm.externalLink || !!vm.aitrusRedirect;
  }

  /**
   * Só folhas liberadas, e só com o menu aberto: na trilha recolhida não há onde pôr o
   * botão, e fixar o que não se pode abrir não serviria para nada. Também espera o
   * e-mail do usuário: antes dele o favorito não teria dono e se perderia.
   */
  public podeFavoritar(vm: MenuItemViewModel, state: DisplayState | null): boolean {
    return state === DisplayState.Enabled && !vm.submenu && !this.collapsed() && this.favoritos.pronto();
  }

  public alternarFavorito(vm: MenuItemViewModel): void {
    if (this.favoritos.alternar(vm.original)) {
      return;
    }

    this.notificationService.sendNotification([{
      type: 'warning',
      title: `Você já tem ${MenuFavoritesService.LIMITE} atalhos fixados`,
      message: 'Desafixe um antes de fixar outro.'
    }]);
  }

  /** Item bloqueado (`blockedReason$`) não abre o destino; o clique só explica o motivo. */
  private _notifyBlocked(vm: MenuItemViewModel, blockedReason: string): void {
    this.notificationService.sendNotification([{
      type: 'blocked',
      title: vm.title,
      message: blockedReason
    }]);
  }

  private _openExternalDestination(vm: MenuItemViewModel): void {
    if (vm.aitrusRedirect) {
      this.aitrusSso.abrir(vm.aitrusRedirect).subscribe({
        // A aba só abre com token válido; sem ele o usuário precisa saber por quê.
        error: () => this.notificationService.sendNotification([{
          type: 'error',
          title: 'Não foi possível abrir a Aitrus',
          message: 'Tente novamente em instantes. Se persistir, entre em contato com o suporte.'
        }])
      });
    } else if (vm.externalLink) {
      window.open(vm.externalLink, '_blank');
    }
  }

  private _closeOnMobile(): void {
    if (this.breakpointObs.isMatched('(max-width: 768px)')) {
      this.close.emit();
    }
  }

  public logout(): void {
    this.userService.logout();
  }
}
